import { useEffect, useRef, useState, Fragment } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

function dot(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 0 4px ${color}33,0 2px 6px rgba(0,0,0,.4);cursor:pointer"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}
function pin(color, glyph) {
  return L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};box-shadow:0 3px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;border:2px solid #fff"><span style="transform:rotate(45deg);color:#fff;font-size:12px;font-weight:700">${glyph}</span></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });
}
const VEHICLE_COLOR = { moto: "#F26419", car: "#1976D2", bike: "#16a34a" };
const VEHICLE_LABEL = { moto: "🏍️ Moto", car: "🚗 Voiture", bike: "🚲 Vélo" };
const ICON_CACHE = {};
function driverIcon(vt) {
  const key = VEHICLE_COLOR[vt] ? vt : "moto";
  if (!ICON_CACHE[key]) ICON_CACHE[key] = dot(VEHICLE_COLOR[key]);
  return ICON_CACHE[key];
}
const AGENT_ICON = dot("#7C3AED");
const OFFLINE_ICON = dot("#9ca3af");
const PICKUP_PIN = pin("#F26419", "A");
const DEST_PIN = pin("#D94F2B", "B");

function ago(iso) {
  if (!iso) return null;
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h`;
}

function Recenter({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    const pts = (points || []).filter((p) => p.lat != null && p.lng != null);
    if (pts.length) {
      map.fitBounds(pts.map((p) => [p.lat, p.lng]), { padding: [60, 60], maxZoom: 14 });
      done.current = true;
    }
  }, [points, map]);
  return null;
}

function popupHtml(p) {
  const seen = ago(p.updated_at);
  const stale = p.updated_at && (Date.now() - new Date(p.updated_at).getTime()) / 1000 > 30;
  const veh = p.role === "agent" ? "" : `<div data-testid="live-vehicle-${p.id}" style="font-size:13px;font-weight:700;color:#1A1A1A;margin-bottom:4px">${VEHICLE_LABEL[p.vehicle_type] || "🏍️ Moto"}</div>`;
  const route = p.on_ride ? `<div data-testid="live-route-${p.id}" style="margin-top:6px;font-size:12px;color:#333;border-top:1px solid #eee;padding-top:6px"><div>🅰️ ${p.ride_pickup_name || "Départ"}</div><div>🅱️ ${p.ride_dest_name || "Destination"}</div></div>` : "";
  const acc = p.accuracy != null ? `<div data-testid="live-acc-${p.id}" style="font-size:11px;color:${p.accuracy > 1000 ? "#D32F2F" : "#75807A"}">📍 Précision ~${Math.round(p.accuracy)} m${p.accuracy > 1000 ? " (imprécis)" : ""}</div>` : "";
  return `<div style="min-width:170px" data-testid="live-popup-${p.id}">
    <div style="font-weight:700;font-size:15px">${p.name || ""}</div>
    <div style="font-size:12px;color:#666;margin-bottom:4px">${p.role === "agent" ? "Agent recruteur" : (p.on_ride ? "🛵 En course" : (p.online === false ? "⚪ Hors ligne · dernière position" : "🟢 Chauffeur en ligne"))}</div>
    ${veh}
    <a href="tel:${p.phone || ""}" style="color:#D94F2B;font-weight:700;font-size:14px">${p.phone || ""}</a>
    <div data-testid="live-coords-${p.id}" style="font-size:11px;color:#75807A;margin-top:2px">📌 ${p.lat != null ? p.lat.toFixed(5) : "?"}, ${p.lng != null ? p.lng.toFixed(5) : "?"}</div>
    ${route}
    <div data-testid="live-seen-${p.id}" style="margin-top:6px;font-size:12px;font-weight:600;color:${seen ? (stale ? "#D32F2F" : "#16a34a") : "#75807A"}">${seen ? `${stale ? "🔴" : "🟢"} Vu il y a ${seen}` : "◽ Position de départ"}</div>
    ${acc}
  </div>`;
}

function Clusters({ points }) {
  const map = useMap();
  useEffect(() => {
    const group = L.markerClusterGroup({ maxClusterRadius: 45, showCoverageOnHover: false, spiderfyOnMaxZoom: true });
    (points || []).forEach((p) => {
      if (p.lat == null || p.lng == null) return;
      const icon = p.role === "agent" ? AGENT_ICON : (p.online === false ? OFFLINE_ICON : driverIcon(p.vehicle_type));
      const m = L.marker([p.lat, p.lng], { icon });
      m.bindPopup(popupHtml(p), { minWidth: 180 });
      group.addLayer(m);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [points, map]);
  return null;
}

export default function LiveMap({ points, center, height = 560 }) {
  const c = center || { lat: 3.848, lng: 11.502 };
  const [layer, setLayer] = useState("plan");
  const [filters, setFilters] = useState({ moto: true, car: true, bike: true, agent: true });
  const mapRef = useRef(null);
  const keyOf = (p) => (p.role === "agent" ? "agent" : (VEHICLE_COLOR[p.vehicle_type] ? p.vehicle_type : "moto"));
  const filtered = (points || []).filter((p) => p.lat != null && p.lng != null && filters[keyOf(p)]);
  const routes = filtered.filter((p) => p.on_ride && p.ride_pickup && p.ride_dest);
  const recenter = () => {
    const m = mapRef.current;
    const pts = filtered.map((p) => [p.lat, p.lng]);
    if (m && pts.length) m.fitBounds(pts, { padding: [60, 60], maxZoom: 14 });
  };
  const count = (k) => (points || []).filter((p) => p.lat != null && p.lng != null && keyOf(p) === k).length;
  const FILT = [["moto", "🏍️", "Motos"], ["car", "🚗", "Voitures"], ["bike", "🚲", "Vélos"], ["agent", "🧑‍💼", "Agents"]];
  const wrapRef = useRef(null);
  const toggleFull = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    else (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
  };
  return (
    <div ref={wrapRef} style={{ height, width: "100%", borderRadius: 20, overflow: "hidden", position: "relative", background: "#fff" }} className="border border-[#E2DFD8]" data-testid="live-map">
      <button data-testid="map-fullscreen" onClick={toggleFull} title="Plein écran"
        style={{ position: "absolute", bottom: 24, left: 12, zIndex: 1000 }}
        className="px-3 h-8 rounded-full bg-white text-[#1A1A1A] text-xs font-bold shadow-md border border-black/10 flex items-center gap-1 hover:bg-[#F26419] hover:text-white transition-colors">⛶ Plein écran</button>
      <div style={{ position: "absolute", top: 12, right: 12, zIndex: 1000 }} className="flex rounded-full overflow-hidden shadow-md border border-black/10">
        <button data-testid="map-layer-plan" onClick={() => setLayer("plan")}
          className={`px-3 h-8 text-xs font-bold ${layer === "plan" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#1A1A1A]"}`}>Plan</button>
        <button data-testid="map-layer-sat" onClick={() => setLayer("sat")}
          className={`px-3 h-8 text-xs font-bold ${layer === "sat" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#1A1A1A]"}`}>Satellite</button>
      </div>
      <div data-testid="map-filters" style={{ position: "absolute", top: 12, left: 52, zIndex: 1000, maxWidth: "calc(100% - 64px)" }} className="flex flex-wrap gap-1.5">
        {FILT.map(([k, glyph, label]) => (
          <button key={k} data-testid={`map-filter-${k}`} onClick={() => setFilters((f) => ({ ...f, [k]: !f[k] }))}
            title={`${label} (${count(k)})`}
            className={`h-8 px-2.5 rounded-full text-xs font-bold shadow-md border border-black/10 flex items-center gap-1 transition-colors ${filters[k] ? "bg-[#1A1A1A] text-white" : "bg-white/90 text-[#75807A]"}`}>
            <span>{glyph}</span><span className="tabular-nums">{count(k)}</span>
          </button>
        ))}
        <button data-testid="map-recenter" onClick={recenter} title="Recentrer sur les agents"
          className="h-8 px-2.5 rounded-full text-xs font-bold shadow-md border border-black/10 bg-white text-[#1A1A1A] flex items-center gap-1 hover:bg-[#F26419] hover:text-white transition-colors">
          🎯 Recentrer
        </button>
      </div>
      <MapContainer ref={mapRef} center={[c.lat, c.lng]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
        {layer === "sat" ? (
          <>
            <TileLayer attribution="Tiles &copy; Esri" url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxZoom={19} />
            <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png" />
          </>
        ) : (
          <TileLayer attribution="&copy; OpenStreetMap, &copy; CARTO" url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" maxZoom={20} />
        )}
        <Recenter points={filtered} />
        {routes.map((p) => (
          <Fragment key={`route-${p.id}`}>
            <Polyline
              positions={[[p.ride_pickup.lat, p.ride_pickup.lng], [p.lat, p.lng], [p.ride_dest.lat, p.ride_dest.lng]]}
              pathOptions={{ color: "#D94F2B", weight: 4, dashArray: "8 8" }}
            />
            <Marker position={[p.ride_pickup.lat, p.ride_pickup.lng]} icon={PICKUP_PIN} />
            <Marker position={[p.ride_dest.lat, p.ride_dest.lng]} icon={DEST_PIN} />
          </Fragment>
        ))}
        <Clusters points={filtered} />
      </MapContainer>
    </div>
  );
}
